// Story 11.5 — Paleta de comandos global (Cmd+K). La pure-logic de
// fuzzy-filter + recientes vive en `lib/command-palette.ts`. Este componente
// solo renderiza UI + maneja teclado.

import { useNavigate } from '@tanstack/react-router';
import { Compass, FileCode2, FileType, GitBranch, Search, Zap } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { useBots } from '@/hooks/queries/use-bots';
import { useFiles } from '@/hooks/queries/use-files';
import {
  type CommandCategory,
  type CommandItem,
  filterCommands,
  groupByCategory,
  loadRecent,
  parseQuery,
  type StorageLike,
  saveRecent,
} from '@/lib/command-palette';
import { isFlowPath } from '@/lib/flow-extraction';
import { cn } from '@/lib/utils';
import { findSnippet, YAML_SNIPPETS } from '@/lib/yaml-snippets';
import { useEditorStore } from '@/stores/editor-store';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Bot context (cuando estamos en `/bots/:id/...`). null en `/`. */
  botId: number | null;
  /** Callback opcional cuando se selecciona una acción global. */
  onAction?: (id: string) => void;
  /** Storage para recents — inyectable para tests. Default: localStorage. */
  storage?: StorageLike;
}

const CATEGORY_LABEL: Record<CommandCategory, string> = {
  files: 'Files',
  navigation: 'Navigation',
  flows: 'Flows',
  actions: 'Actions',
  templates: 'Templates',
};

const CATEGORY_ICON: Record<CommandCategory, React.ComponentType<{ className?: string }>> = {
  files: FileCode2,
  navigation: Compass,
  flows: GitBranch,
  actions: Zap,
  templates: FileType,
};

const CATEGORY_ORDER: CommandCategory[] = ['actions', 'files', 'flows', 'navigation', 'templates'];

const PREFIX_LABEL: Record<string, string> = {
  '>': 'Actions',
  ':': 'Files',
  '@': 'Flows',
  '#': 'Navigation',
};

interface CommandAction {
  item: CommandItem;
  run: () => void;
}

export function CommandPalette({
  open,
  onClose,
  botId,
  onAction,
  storage,
}: Props): React.ReactElement | null {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sin bot en la ruta no hay árbol que pedir (NaN deja la query apagada).
  const filesQuery = useFiles(botId ?? Number.NaN);
  const botsQuery = useBots();
  const openTab = useEditorStore((s) => s.openTab);
  const toggleDiagram = useEditorStore((s) => s.toggleDiagram);
  const toggleLogs = useEditorStore((s) => s.toggleLogs);

  // Storage backend — default `window.localStorage` con guard de SSR.
  const storageImpl = useMemo<StorageLike>(() => {
    if (storage) return storage;
    if (typeof window === 'undefined') {
      return { getItem: () => null, setItem: () => undefined };
    }
    return window.localStorage;
  }, [storage]);

  const [recentIds, setRecentIds] = useState<string[]>([]);
  useEffect(() => {
    if (!open) return;
    setRecentIds(loadRecent(storageImpl, botId));
  }, [open, botId, storageImpl]);

  // Reset state al abrir.
  useEffect(() => {
    if (open) {
      setInput('');
      setHighlighted(0);
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Construir items por categoría.
  const actions = useMemo<CommandAction[]>(() => {
    const list: CommandAction[] = [];
    if (botId != null) {
      list.push(
        {
          item: {
            id: 'action:deploy',
            category: 'actions',
            label: 'Deploy',
            keywords: ['ship', 'release'],
            hint: 'Deploy current config',
          },
          run: () => onAction?.('deploy'),
        },
        {
          item: {
            id: 'action:rollback',
            category: 'actions',
            label: 'Rollback',
            keywords: ['undo'],
          },
          run: () => onAction?.('rollback'),
        },
        {
          item: {
            id: 'action:save-all',
            category: 'actions',
            label: 'Save All',
            hint: 'Cmd+Shift+S',
          },
          run: () => onAction?.('save-all'),
        },
        {
          item: {
            id: 'action:toggle-diagram',
            category: 'actions',
            label: 'Toggle Diagram',
            hint: 'Cmd+Shift+D',
          },
          run: () => toggleDiagram(),
        },
        {
          item: {
            id: 'action:toggle-logs',
            category: 'actions',
            label: 'Toggle Logs',
            hint: 'Cmd+J',
          },
          run: () => toggleLogs(),
        },
        {
          item: {
            id: 'action:open-in-cli',
            category: 'actions',
            label: 'Open in CLI (copy command)',
          },
          run: () => onAction?.('open-in-cli'),
        },
      );
    }
    list.push({
      item: {
        id: 'action:view-docs',
        category: 'actions',
        label: 'View documentation',
      },
      run: () => onAction?.('view-docs'),
    });
    return list;
  }, [botId, onAction, toggleDiagram, toggleLogs]);

  const navigation = useMemo<CommandAction[]>(() => {
    const list: CommandAction[] = [
      {
        item: { id: 'nav:dashboard', category: 'navigation', label: 'Dashboard' },
        run: () => {
          // biome-ignore lint/suspicious/noExplicitAny: TanStack search relaxed.
          void navigate({ to: '/' as any });
        },
      },
    ];
    if (botId != null) {
      const items: Array<{ id: string; label: string; to: string }> = [
        { id: 'nav:bot', label: 'Bot overview', to: `/bots/${botId}` },
        {
          id: 'nav:conversations',
          label: 'Conversations',
          to: `/bots/${botId}/conversations`,
        },
        { id: 'nav:analytics', label: 'Analytics', to: `/bots/${botId}/analytics` },
        { id: 'nav:editor', label: 'Editor', to: `/bots/${botId}/editor` },
        { id: 'nav:settings', label: 'Settings', to: `/bots/${botId}/settings` },
      ];
      for (const it of items) {
        list.push({
          item: { id: it.id, category: 'navigation', label: it.label },
          run: () => {
            // biome-ignore lint/suspicious/noExplicitAny: TanStack search relaxed.
            void navigate({ to: it.to as any });
          },
        });
      }
    } else {
      // Lista de bots cuando no hay context.
      for (const bot of botsQuery.data ?? []) {
        list.push({
          item: {
            id: `nav:bot-${bot.id}`,
            category: 'navigation',
            label: `Bot: ${bot.name}`,
          },
          run: () => {
            // biome-ignore lint/suspicious/noExplicitAny: TanStack search relaxed.
            void navigate({ to: `/bots/${bot.id}` as any });
          },
        });
      }
    }
    return list;
  }, [botId, navigate, botsQuery.data]);

  const files = useMemo<CommandAction[]>(() => {
    if (botId == null) return [];
    const tree = filesQuery.data ?? [];
    const flat: CommandAction[] = [];
    function walk(nodes: typeof tree): void {
      for (const n of nodes) {
        if (n.type === 'file') {
          flat.push({
            item: {
              id: `file:${n.path}`,
              category: 'files',
              label: n.path,
              keywords: [n.name],
            },
            run: () => openTab({ path: n.path, content: '', readOnly: n.readOnly }),
          });
        } else {
          walk(n.children);
        }
      }
    }
    walk(tree);
    return flat;
  }, [filesQuery.data, botId, openTab]);

  const flows = useMemo<CommandAction[]>(() => {
    if (botId == null) return [];
    // P-469 (review 11.5): walk recursivo + `isFlowPath` para soportar
    // flows nested (`flows/onboarding/welcome.yaml`). Antes filtraba solo
    // top-level `flows/*.yaml` y dropeaba subdirectorios silenciosamente.
    const out: CommandAction[] = [];
    function walkFlows(nodes: typeof filesQuery.data | undefined): void {
      if (!nodes) return;
      for (const node of nodes) {
        if (node.type === 'dir') {
          walkFlows(node.children as typeof filesQuery.data);
          continue;
        }
        if (!isFlowPath(node.path)) continue;
        out.push({
          item: {
            id: `flow:${node.path}`,
            category: 'flows' as const,
            label: node.name.replace(/\.yaml$/, ''),
            keywords: [node.path],
          },
          run: () => openTab({ path: node.path, content: '', readOnly: false }),
        });
      }
    }
    walkFlows(filesQuery.data);
    return out;
  }, [filesQuery.data, botId, openTab]);

  // Story 11.5 AC #5: Templates solo si el archivo activo es flow YAML.
  // P-468 (review 11.5): reutilizamos `isFlowPath` (helper canónico) en
  // vez del regex inline que rechazaba paths nested.
  const activePath = useEditorStore((s) => s.activePath);
  const isOnFlow = activePath != null && isFlowPath(activePath);
  const templates = useMemo<CommandAction[]>(() => {
    if (!isOnFlow) return [];
    return YAML_SNIPPETS.map((snippet) => ({
      item: {
        id: `tpl:${snippet.id}`,
        category: 'templates' as const,
        label: snippet.name,
        keywords: [snippet.description],
      },
      run: () => onAction?.(`insert-snippet:${snippet.id}`),
    }));
  }, [isOnFlow, onAction]);

  const allActions = useMemo(
    () => [...actions, ...navigation, ...files, ...flows, ...templates],
    [actions, navigation, files, flows, templates],
  );

  const filtered = useMemo(() => {
    return filterCommands(
      allActions.map((a) => a.item),
      input,
      recentIds,
    );
  }, [allActions, input, recentIds]);

  // Mantener highlight dentro del rango — reset cuando cambia el input.
  // biome-ignore lint/correctness/useExhaustiveDependencies: input es el trigger
  useEffect(() => {
    setHighlighted(0);
  }, [input]);

  const grouped = useMemo(() => groupByCategory(filtered), [filtered]);

  const orderedItems = useMemo(() => {
    const out: CommandItem[] = [];
    for (const cat of CATEGORY_ORDER) {
      const list = grouped.get(cat);
      if (!list) continue;
      out.push(...list);
    }
    return out;
  }, [grouped]);

  const parsed = useMemo(() => parseQuery(input), [input]);

  function execItem(item: CommandItem): void {
    const action = allActions.find((a) => a.item.id === item.id);
    if (!action) return;
    action.run();
    setRecentIds(saveRecent(storageImpl, botId, item.id));
    onClose();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((v) => Math.min(v + 1, Math.max(0, orderedItems.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((v) => Math.max(0, v - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = orderedItems[highlighted];
      if (item) execItem(item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  }

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center bg-background/70 p-4 pt-[15vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Paleta de comandos"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div className="w-full max-w-xl overflow-hidden rounded-md border border-border bg-card shadow-2xl">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            ref={inputRef}
            type="search"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              parsed.prefix
                ? `${PREFIX_LABEL[parsed.prefix]}: type to filter…`
                : 'Type a command or search… (>, :, @, # to filter)'
            }
            aria-label="Buscar comandos"
            className="h-8 border-0 px-0 focus-visible:ring-0"
          />
          {parsed.prefix && (
            <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
              {PREFIX_LABEL[parsed.prefix]}
            </span>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {orderedItems.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-muted-foreground">
              No matches. Tip: try <code className="font-mono">&gt;</code> for actions only.
            </p>
          ) : (
            CATEGORY_ORDER.map((cat) => {
              const list = grouped.get(cat);
              if (!list || list.length === 0) return null;
              const Icon = CATEGORY_ICON[cat];
              return (
                <div key={cat}>
                  <p className="bg-muted/30 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {CATEGORY_LABEL[cat]}
                  </p>
                  <ul>
                    {list.map((item) => {
                      const indexInOrdered = orderedItems.findIndex((i) => i.id === item.id);
                      const isHighlighted = indexInOrdered === highlighted;
                      return (
                        <li key={item.id}>
                          <button
                            data-highlighted={isHighlighted}
                            type="button"
                            onClick={() => execItem(item)}
                            onMouseEnter={() => setHighlighted(indexInOrdered)}
                            className={cn(
                              'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                              isHighlighted
                                ? 'bg-accent text-accent-foreground'
                                : 'hover:bg-muted/30',
                            )}
                          >
                            <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
                            <span className="flex-1 truncate">{item.label}</span>
                            {item.hint && (
                              <span className="text-[10px] text-muted-foreground">{item.hint}</span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

/** Pequeño helper para que callers puedan resolver el snippet por id. */
export function snippetIdFromActionId(actionId: string): string | null {
  if (!actionId.startsWith('insert-snippet:')) return null;
  const id = actionId.slice('insert-snippet:'.length);
  return findSnippet(id) ? id : null;
}
